import { keepPreviousData, useQuery } from '@tanstack/react-query'

import type { ProductCategory, ProductType } from '@snootsvet/shared'

import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'

interface UseListProductsWithStockParams {
  q?: string
  category?: ProductCategory
  type?: ProductType[]
  limit?: number
  offset?: number
  active?: boolean
  enabled?: boolean
}

/**
 * Fetches products (with per-clinic stock) in the active region, paginated.
 * Refetches on every search and mount, since stock moves faster than the default cache.
 */
export function useListProductsWithStock(params: UseListProductsWithStockParams = {}) {
  const api = useOsApi()
  const { region } = useViewerRegion()
  const { q, category, type, limit, offset, active, enabled } = params

  return useQuery({
    queryKey: ['products', { include: 'stock', q, category, type, limit, offset, active, region }],
    enabled,
    queryFn: async () => {
      const response = await api.get('/products', {
        query: { include: 'stock', q, category, type, limit, offset, active, region },
      })
      if (!response.success) throw new Error(response.error.message)
      const { products, totalCount } = response.data
      return {
        products: products.map((product) => ({ ...product, stock: product.stock ?? [] })),
        totalCount,
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
  })
}
